import React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { FiSend } from "react-icons/fi";
import type { Row as TanStack } from "@tanstack/react-table";
import type { ReportMetricRow } from "@core/components/ReportTreeTable";
import type { DepartmentReportMediaItem } from "@core/reports/api/types";
import type { DepartmentReportScope } from "@core/reports/api/requestDepartmentReportSave";
import { parseReportViewSearch } from "@core/reports/constants/reportSpaQueryParams";
import { useReportPage } from "@core/reports/hooks/useReportPage";
import ReportPageTemplate, {
  type TemplatePreviewContext,
} from "@core/reports/components/ReportPageTemplate";
import RequestReviewModal from "@core/reports/components/RequestReviewModal";
import RollupReportMembershipSection from "@features/membership/components/RollupReportMembershipSection";
import TemplateRowPreviewButton from "@core/reports/components/TemplateRowPreviewButton";
import ChildMediaPopover from "@core/reports/components/ChildMediaPopover";
import { requestReportReviewAsync } from "@core/reports/api/requestReportReview";
import {
  canRequestChildDepartmentReportReview,
  isAncestorOpenForChildReview,
} from "@core/reports/utils/reportWorkflow";

const iconBtnStyle = (disabled: boolean): React.CSSProperties => ({
  width: 28,
  height: 28,
  border: "1px solid var(--ntcog-border)",
  borderRadius: "var(--ntcog-radius-sm)",
  background: disabled ? "var(--ntcog-surface)" : "var(--ntcog-input)",
  cursor: disabled ? "not-allowed" : "pointer",
  lineHeight: 1,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flex: "0 0 auto",
});

export default function NationReportPage() {
  const { nationId, departmentId: urlDepartmentId } = useParams<{ nationId: string; departmentId: string }>();
  const [searchParams] = useSearchParams();
  const numericNationId = Number(nationId) || 0;
  const { departmentId: queryDepartmentId, reportTagId } = parseReportViewSearch(searchParams);
  const departmentId = Number(urlDepartmentId) || queryDepartmentId;

  const report = useReportPage({
    scope: "nation",
    entityId: numericNationId,
    departmentId,
    reportTagId,
  });
  const {
    canLoad,
    departmentReportId,
    statusName,
    load,
    loadDepartmentReport,
    permissionFlags,
    permissionsLoading,
  } = report;

  const showRowReview = permissionFlags.showReview && !permissionsLoading;
  const parentOpenForChildReview =
    isAncestorOpenForChildReview(departmentReportId, statusName) &&
    (report.meta as { actions_locked?: boolean } | null)?.actions_locked !== true;

  const [showReviewModal, setShowReviewModal] = React.useState(false);
  const [reviewDistrict, setReviewDistrict] = React.useState<{
    id: number;
    name: string;
    reportId: number | null;
  } | null>(null);
  const [requestingReview, setRequestingReview] = React.useState(false);
  const [reviewError, setReviewError] = React.useState<string | null>(null);

  const handleDistrictRequestReview = async ({ reason }: { reason: string }) => {
    if (!reviewDistrict?.reportId) {
      setReviewError("Missing department report id for the selected district.");
      return;
    }
    setRequestingReview(true);
    setReviewError(null);
    try {
      const result = await requestReportReviewAsync({
        scope: "district",
        districtId: reviewDistrict.id,
        departmentId,
        departmentReportId: reviewDistrict.reportId,
        notes: reason.slice(0, 255),
      });
      if (!result.isSuccess()) throw new Error(result.getError()?.detail || "Failed to request review.");
      setShowReviewModal(false);
      await load();
      await loadDepartmentReport();
    } catch (e) {
      setReviewError(e instanceof Error ? e.message : "Failed to request review.");
    } finally {
      setRequestingReview(false);
    }
  };

  const renderRowName = React.useCallback(
    (row: TanStack<ReportMetricRow>, ctx: TemplatePreviewContext) => {
      const original: any = row.original ?? {};
      const name = String(original.name ?? "").trim() || "—";
      const churchId = Number(original.church_id ?? original.__meta?.church_id ?? 0) || 0;
      const districtId = Number(original.district_id ?? original.__meta?.district_id ?? 0) || 0;
      const childReportId = Number(original.department_report_id ?? 0) || null;
      const childMedia: DepartmentReportMediaItem[] = Array.isArray(original.media)
        ? (original.media as DepartmentReportMediaItem[])
        : [];
      const rowType = original.__rowType as string | undefined;
      const templateId = Number(original.template_id ?? original.__meta?.template_id ?? 0) || 0;

      const isTemplateLeaf =
        rowType === "template" ||
        rowType === "sub_group" ||
        (templateId > 0 && row.depth > 0);
      const isDistrictRow = !isTemplateLeaf && districtId > 0 && churchId === 0;
      const childScope: DepartmentReportScope | null = isTemplateLeaf
        ? null
        : churchId > 0
          ? "church"
          : districtId > 0
            ? "district"
            : null;
      const childEntityId = churchId > 0 ? churchId : districtId;

      const districtStatus =
        original.district_report_status_name ?? original.report_status_name ?? null;
      const districtStatusAllowsReview =
        !districtStatus || canRequestChildDepartmentReportReview(districtStatus);
      const showDistrictReview =
        isDistrictRow &&
        showRowReview &&
        parentOpenForChildReview &&
        !!childReportId &&
        districtStatusAllowsReview;
      const reviewDisabled = requestingReview || !canLoad || !childReportId || districtId <= 0;

      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "nowrap",
            minWidth: 0,
            width: "100%",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, flex: "0 0 auto" }}>
            {isTemplateLeaf ? (
              <TemplateRowPreviewButton
                onPreview={() => ctx.onPreview({ name: String(original.name ?? "Template"), row: original })}
              />
            ) : null}
            {showDistrictReview ? (
              <button
                type="button"
                title="Request review"
                disabled={reviewDisabled}
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  setReviewDistrict({ id: districtId, name, reportId: childReportId });
                  setReviewError(null);
                  setShowReviewModal(true);
                }}
                style={iconBtnStyle(reviewDisabled)}
              >
                <FiSend />
              </button>
            ) : null}
          </div>
          <span
            style={{
              flex: "1 1 auto",
              minWidth: 0,
              whiteSpace: "normal",
              overflowWrap: "break-word",
              lineHeight: 1.25,
            }}
            title={name}
          >
            {name}
          </span>
          {childScope && childEntityId > 0 ? (
            <span style={{ flex: "0 0 auto" }}>
              <ChildMediaPopover
                scope={childScope}
                entityId={childEntityId}
                departmentId={departmentId}
                departmentReportId={childReportId}
                media={childMedia}
              />
            </span>
          ) : null}
        </div>
      );
    },
    [
      departmentId,
      showRowReview,
      parentOpenForChildReview,
      requestingReview,
      canLoad,
    ]
  );

  return (
    <ReportPageTemplate
      report={report}
      scopeNoun="nation"
      renderRowName={renderRowName}
      membershipSection={
        canLoad ? (
          <RollupReportMembershipSection
            scope="nation"
            scopeId={numericNationId}
            departmentId={departmentId}
            reportTagId={reportTagId}
            reloadToken={statusName ?? ""}
          />
        ) : null
      }
    >
      <RequestReviewModal
        show={showReviewModal}
        onHide={() => {
          if (!requestingReview) setShowReviewModal(false);
        }}
        subject={
          <>
            You are requesting review for:{" "}
            <span className="fw-semibold">{reviewDistrict?.name ?? "District"}</span>
          </>
        }
        busy={requestingReview}
        error={reviewError}
        onConfirm={handleDistrictRequestReview}
      />
    </ReportPageTemplate>
  );
}
